// CategorySubcategoryList.js

import React, { useEffect, useMemo, useState } from "react";

import { Card, Table, Badge, Collapse, Spinner } from "react-bootstrap";

import {
  FiEdit2,
  FiTrash2,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";

import styles from "./CategorySubcategoryList.module.css";

import { getImageUrl } from "../../../MIS/Global";
import CommonActionMenu from "../../Common/CommonAction/CommonActionMenu";

const CategorySubcategoryList = ({
  data = [],
  onCategoryEdit,
  onCategoryDelete,
  onSubCategoryEdit,
  onSubCategoryDelete,
  loading = false,
  actionLoading,
  search = "",
}) => {
  const [openRow, setOpenRow] = useState(null);

  // ================= SEARCH =================

  const filteredData = useMemo(() => {
    const searchText = search?.toLowerCase()?.trim();

    if (!searchText) {
      return data?.map((item) => ({
        ...item,
        filteredSubCategories: item?.sub_categories || [],
      }));
    }

    return data
      ?.map((item) => {
        const categoryMatch = item?.name
          ?.toLowerCase()
          ?.includes(searchText);

        const filteredSubCategories =
          item?.sub_categories?.filter((sub) =>
            sub?.name?.toLowerCase()?.includes(searchText)
          ) || [];

        if (categoryMatch || filteredSubCategories?.length > 0) {
          return {
            ...item,
            filteredSubCategories:
              filteredSubCategories?.length > 0
                ? filteredSubCategories
                : item?.sub_categories || [],
          };
        }

        return null;
      })
      ?.filter(Boolean);
  }, [data, search]);

  // ================= AUTO OPEN =================

  useEffect(() => {
    if (!search?.trim()) {
      setOpenRow(null);

      return;
    }

    if (filteredData?.length > 0) {
      setOpenRow(filteredData?.[0]?._id);
    }
  }, [search, filteredData]);

  // ================= TOGGLE =================
  // only one row open at a time

  const toggleRow = (id) => {
    setOpenRow((prev) => (prev === id ? null : id));
  };

  return (
    <Card className={styles.mainCard}>
      <Card.Body className={styles.cardBody}>
        <div className={styles.tableWrapper}>
          <Table borderless className="mb-0">
            <thead>
              <tr>
                <th className={styles.stickyColumn}>Category</th>

                <th>Status</th>

                <th align="right"></th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} className={styles.loaderWrapper}>
                    <div className={styles.loaderContent}>
                      <Spinner animation="border" variant="primary" />

                      <div className={styles.loaderText}>
                        Loading categories...
                      </div>
                    </div>
                  </td>
                </tr>
              ) : filteredData?.length > 0 ? (
                filteredData?.map((item, index) => {
                  const isOpen = openRow === item?.id;

                  return (
                    <React.Fragment key={item?.id}>
                      {/* CATEGORY ROW */}

                      <tr
                        className={`${styles.categoryRow} ${
                          index % 2 === 0
                            ? styles.categoryRowEven
                            : styles.categoryRowOdd
                        }`}
                        onClick={() => toggleRow(item?.id)}
                      >
                        <td className={styles.stickyColumn}>
                          <div className={styles.categoryInfo}>
                            <img
                              src={getImageUrl(item?.img_path)}
                              alt={item?.name}
                              className={styles.categoryImage}
                            />

                            <div className={styles.categoryContent}>
                              <div className={styles.categoryName}>
                                {item?.name}
                              </div>

                              <div className={styles.categoryMeta}>
                                {item?.sub_categories?.length} Sub Categories
                              </div>
                            </div>

                            <div className={styles.expandIcon}>
                              {isOpen ? (
                                <FiChevronUp />
                              ) : (
                                <FiChevronDown />
                              )}
                            </div>
                          </div>
                        </td>

                        {/* STATUS */}

                        <td>
                          <Badge
                            bg={item?.is_active ? "success" : "secondary"}
                            className={styles.statusBadge}
                          >
                            {item?.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </td>

                        {/* CATEGORY ACTION */}

                        <td align="right" onClick={(e) => e.stopPropagation()}>
                          {parseInt(actionLoading?.delId) ===
                          parseInt(item?.id) ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Deleting...
                            </>
                          ) : (
                            <CommonActionMenu
                              actions={[
                                {
                                  label: "Edit",
                                  icon: <FiEdit2 />,
                                  onClick: () => onCategoryEdit?.(item),
                                },
                                {
                                  label: "Delete",
                                  icon: <FiTrash2 />,
                                  danger: true,
                                  onClick: () => onCategoryDelete?.(item),
                                },
                              ]}
                            />
                          )}
                        </td>
                      </tr>

                      {/* SUB CATEGORY */}

                      <tr>
                        <td colSpan={3} className="p-0 border-0">
                          <Collapse in={isOpen}>
                            <div className={styles.childWrapper}>
                              {item?.filteredSubCategories?.length > 0 ? (
                                item?.filteredSubCategories?.map(
                                  (sub, subIndex) => (
                                    <div
                                      key={sub?._id}
                                      className={`${styles.childItem} ${
                                        subIndex % 2 === 0
                                          ? styles.childItemEven
                                          : styles.childItemOdd
                                      }`}
                                    >
                                      {/* LEFT */}

                                      <div className={styles.childLeft}>
                                        <img
                                          src={getImageUrl(sub?.img_path)}
                                          alt={sub?.name}
                                          className={styles.subImage}
                                        />

                                        <div className={styles.subName}>
                                          {sub?.name}
                                        </div>
                                      </div>

                                      {/* RIGHT */}

                                      <div className={styles.childRight}>
                                        <Badge
                                          bg={
                                            sub?.is_active
                                              ? "success"
                                              : "secondary"
                                          }
                                          className={styles.statusBadge}
                                        >
                                          {sub?.is_active
                                            ? "Active"
                                            : "Inactive"}
                                        </Badge>

                                        <button
                                          className={styles.actionBtn}
                                          onClick={() =>
                                            onSubCategoryEdit?.({
                                              ...sub,
                                              category: {
                                                value: item?.id,
                                                label: item?.name,
                                              },
                                            })
                                          }
                                        >
                                          <FiEdit2 />
                                        </button>

                                        {parseInt(
                                          actionLoading?.delSubId
                                        ) === parseInt(sub?.id) ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            Deleting...
                                          </>
                                        ) : (
                                          <button
                                            className={`${styles.actionBtn} ${styles.deleteBtn}`}
                                            onClick={() =>
                                              onSubCategoryDelete?.(sub)
                                            }
                                          >
                                            <FiTrash2 />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  )
                                )
                              ) : (
                                <div className={styles.noData}>
                                  No sub categories found
                                </div>
                              )}
                            </div>
                          </Collapse>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={3} className={styles.emptyWrapper}>
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
};

export default CategorySubcategoryList;